import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@core/components/toasts/toasts.service';
import { FormAbstact } from 'app/views/pages/abstract/form-abstract';
import { DomainService } from 'services/domain.service';
import { GeographyService } from 'services/geography.service';
import { HiringConfigService } from 'services/hiring/hiring-config.service';
import { HiringConfigModel } from 'services/models/hiring/hiringConfig.model';
import { FileUploaded } from '../../components/upload-file-input/upload-file-input.component';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { CompanyService } from 'services/corporate/company.service';
import { OcrHiringSettingsService } from 'services/hiring/ocr-hiring-settings.service';
import { OcrHiringSettingsModel } from 'services/models/hiring/ocr-hiring-settings.model';
import { HttpEventType } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDialogComponent, ConfirmDialogEnum } from 'app/layout/components/confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-hiring-config-form',
    templateUrl: './hiring-config-form.component.html',
    styleUrls: ['./hiring-config-form.component.scss']
})
export class HiringConfigFormComponent
    extends FormAbstact<HiringConfigModel, HiringConfigService>
    implements OnInit, OnChanges {

    @Input() initialConfigs: HiringConfigModel;
    bankOptions: any[] = [];
    sesmtEmailOptions: { label: string; value: string }[] = [];
    dpEmailOptions: { label: string; value: string }[] = [];
    finishedEmailOptions: { label: string; value: string }[] = [];
    public uploadInProgress: boolean = false;
    public uploadProgress: number = 0;
    applicantMetadata: Array<{ key: string, label: string, info?: string }> = [];
    admissionMetadata: Array<{ key: string, label: string, info?: string }> = [];
    drawerState: boolean = false;
    drawerTitle: string = "Configuração de Campos dos Formulários";
    private ocrHiringSettings: OcrHiringSettingsModel = new OcrHiringSettingsModel();

    constructor(
        private service: HiringConfigService,
        private activeRoute: ActivatedRoute,
        private router: Router,
        private toasterService: ToastService,
        private formBuilder: UntypedFormBuilder,
        private _domainService: DomainService,
        private geographyService: GeographyService,
        private _companyService: CompanyService,
        private _modalService: NgbModal,
        private ocrHiringSettingsService: OcrHiringSettingsService
    ) {
        super(service, activeRoute, router, toasterService);
    }

    ngOnInit(): void {
        this.setFormFields();
        this.initializeForm();
        this.setHiringConfigs();
        this.getBanks();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['initialConfigs']?.currentValue || !this.form) return;

        this.setHiringConfigs();
    }

    setHiringConfigs() {
        if (!this.initialConfigs) return;

        const sesmtEmails = this.parseEmails(this.initialConfigs.sesmtAlertEmail || '');
        this.sesmtEmailOptions = this.convertEmailsToOptions(sesmtEmails);
        const dpEmails = this.parseEmails(this.initialConfigs.dpAlertEmail || '');
        this.dpEmailOptions = this.convertEmailsToOptions(dpEmails);
        const finishedEmails = this.parseEmails(this.initialConfigs.finishedAlertEmail || '');
        this.finishedEmailOptions = this.convertEmailsToOptions(finishedEmails);
        this.populateForm();
        this.loadOcrHiringSettings();
    }

    formatBanks(banks: Array<string | number> = []) {
        if (!Array.isArray(banks) || !this.bankOptions?.length) {
            return [];
        }

        const normalized = banks
            .map(x => (x === null || x === undefined ? '' : String(x).trim()))
            .filter(x => x.length);

        return this.bankOptions.filter(option => normalized.includes(String(option.value)));
    }

    getBanks() {
        this.geographyService.getBanks().subscribe((bank) => {
            this.bankOptions = (bank.data || []).map((item: any) => ({
                label: `${item.code} - ${item.name}`,
                value: String(item.code)
            }));

            this.bankOptions.push({ value: "BSC", label: "BSC" })
            this.form.patchValue({
                bankNumbers: this.formatBanks(this.initialConfigs.bankNumbers),
            });
            this.form.updateValueAndValidity();
        });
    }

    initializeForm() {
        const applicantGroup: any = {};
        const admissionGroup: any = {};

        this.applicantMetadata.forEach(f => applicantGroup[f.key] = [1]);
        this.admissionMetadata.forEach(f => admissionGroup[f.key] = [1]);

        this.form = this.formBuilder.group({
            notifyCandidates: [true],
            validateImageQuality: [true],
            finishedAlertEmail: [""],
            sesmtAlertEmail: [""],
            dpAlertEmail: [""],
            offerLetterMessage: [""],
            offerLetterEmail: [""],
            wppCoverImage: [""],
            wppCoverImageFile: [""],
            wppCongratsMessage: [""],
            wppInvalidDocsMessage: [""],
            wppFinishedMessage: [""],
            completionDeadline: [0],
            bankNumbers: [[]],
            asoExamMessage: [""],
            instructionsMp4Url: [""],
            instructionsMP4: [null],
            instructionsPdfUrl: [""],
            instructionsPDF: [null],
            benefitsPDFUrl: [""],
            benefitsPDF: [null],
            setApplicantForm: this.formBuilder.group(applicantGroup), // 1 obrigatório | 2 opcional | 3 não visível
            setAdmissionForm: this.formBuilder.group(admissionGroup),
        });
    }

    populateForm(): void {
        const formatEmails = (emails: string) => emails ? emails.split(";").map(email => email.trim()) : [];
        const patch: any = {
            notifyCandidates: this.initialConfigs.notifyCandidates !== false,
            sesmtAlertEmail: formatEmails(this.initialConfigs.sesmtAlertEmail),
            dpAlertEmail: formatEmails(this.initialConfigs.dpAlertEmail),
            finishedAlertEmail: formatEmails(this.initialConfigs.finishedAlertEmail),
            wppInvalidDocsMessage: this.initialConfigs.wppInvalidDocsMessage || "",
            wppCongratsMessage: this.initialConfigs.wppCongratsMessage || "",
            wppFinishedMessage: this.initialConfigs.wppFinishedMessage || "",
            offerLetterEmail: this.initialConfigs.offerLetterEmail || "",
            offerLetterMessage: this.initialConfigs.offerLetterMessage || "",
            asoExamMessage: this.initialConfigs.asoExamMessage || "",
            completionDeadline: this.initialConfigs.completionDeadline || 72,

            setAdmissionForm: JSON.parse(this.initialConfigs.setAdmissionForm || '{}'),
            setApplicantForm: JSON.parse(this.initialConfigs.setApplicantForm || '{}'),

            benefitsPDF: this.prepareFileFromUrl(this.initialConfigs?.benefitsPDFUrl),
            instructionsPDF: this.prepareFileFromUrl(this.initialConfigs?.instructionsPdfUrl),
            instructionsMP4: this.prepareFileFromUrl(this.initialConfigs?.instructionsMp4Url),
            wppCoverImageFile: this.prepareFileFromUrl(this.initialConfigs?.wppCoverImage),
        };

        if (this.bankOptions?.length) {
            patch.bankNumbers = this.formatBanks(this.initialConfigs.bankNumbers);
        }

        this.form.patchValue(patch);
        this.form.updateValueAndValidity();
    }

    loadOcrHiringSettings(): void {
        if (!this.initialConfigs?.usesOCR) {
            return;
        }

        this.ocrHiringSettingsService.getSetting().subscribe((res) => {
            this.ocrHiringSettings = res?.data || new OcrHiringSettingsModel();
            this.form.patchValue({
                validateImageQuality: this.ocrHiringSettings.validateImageQuality !== false,
            });
        });
    }

    prepareFileFromUrl(url: string | null | undefined): any {
        if (!url) return null;

        const extension = url.split(".").pop()?.toLowerCase() || "";
        const fileName = url.split("/").pop() || "arquivo";

        return {
            fileName: fileName,
            extension: extension,
            previewUrl: url,
            encodedBase64: null,
        };
    }

    parseEmails(emailString: string): string[] {
        return emailString ? emailString.split(';').map(email => email.trim()) : [];
    }

    convertEmailsToOptions(emails: string[]): { label: string; value: string }[] {
        return emails.map(email => ({ label: email, value: email }));
    }

    addEmail = (email: string): any => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
            return { label: email, value: email };
        } else {
            this.toasterService.showWarning('Email inválido. Tente novamente.');
            return null;
        }
    }

    addBank = (bank: string): any => {
        const normalized = (bank || '').trim().toUpperCase();
        if (!normalized) return null;

        const existing = this.bankOptions.find(
            opt => String(opt.value).toUpperCase() === normalized
        );
        return existing ?? { label: normalized, value: normalized };
    }

    onFileUploaded(data: FileUploaded) {
        this.form.get(data.controlName).setValue(data.content);
    }

    handleSubmit(): Observable<any> {
        if (!this.initialConfigs?.id) {
            this.toasterService.showWarning('A configuração da organização ainda não está pronta para salvar.');
            return EMPTY;
        }

        if (this.validaForm()) {
            const sesmtAlertEmailString = (this.form.get('sesmtAlertEmail')?.value || [])
                .map((email: any) => (typeof email === 'object' && email.value ? email.value : email))
                .join(';');

            const dpAlertEmailString = (this.form.get('dpAlertEmail')?.value || [])
                .map((email: any) => (typeof email === 'object' && email.value ? email.value : email))
                .join(';');

            const finishedAlertEmailString = (this.form.get('finishedAlertEmail')?.value || [])
                .map((email: any) => (typeof email === 'object' && email.value ? email.value : email))
                .join(';');

            const { validateImageQuality, ...formValue } = this.form.value;

            this.object = {
                ...formValue,
                sesmtAlertEmail: sesmtAlertEmailString,
                dpAlertEmail: dpAlertEmailString,
                finishedAlertEmail: finishedAlertEmailString,
                bankNumbers: (this.form.get('bankNumbers')?.value || [])
                    .map((item: any) => (typeof item === 'object' && item?.value !== undefined ? item.value : item))
                    .map((item: any) => String(item).trim())
                    .filter((item: string) => item.length),
                setAdmissionForm: JSON.stringify(this.form.get('setAdmissionForm')?.value || {}),
                setApplicantForm: JSON.stringify(this.form.get('setApplicantForm')?.value || {}),
                companyGroupId: this._domainService.getDomain().id,
                organizationId: this.initialConfigs.organizationId,
                id: this.initialConfigs.id
            };

            const hiringSave$ = this.service.saveConfigs(this.object);

            if (!this.initialConfigs?.usesOCR) {
                return hiringSave$;
            }

            const ocrHiringSettings = OcrHiringSettingsModel.create({
                ...this.ocrHiringSettings,
                id: this.ocrHiringSettings?.id,
                validateImageQuality: validateImageQuality !== false,
            });

            return forkJoin({
                hiring: hiringSave$,
                ocr: this.ocrHiringSettingsService.createOrUpdate(ocrHiringSettings),
            });
        }

        return EMPTY;
    }

    onLessonFileSelected(event: Event, featureFolder: string) {
        const input = event.target as HTMLInputElement;

        if (!input.files || input.files.length === 0) return;

        const file = input.files[0];

        const allowedTypes = [
            "video/mp4",
            "video/avi",
            "video/quicktime", // mov
        ];

        if (!allowedTypes.includes(file.type)) {
            this.toasterService.showWarning(
                "Formato do arquivo não é permitido.",
                "top-center"
            );

            input.value = "";

            return;
        }

        this.uploadInProgress = true;

        this.uploadProgress = 0;

        this._companyService
            .uploadFileToPreSignedUrlWithProgress(file, featureFolder)
            .subscribe({
                next: ({ event, fileKey }) => {
                    if (event.type === HttpEventType.UploadProgress && event.total) {
                        this.uploadProgress = Math.round(
                            100 * (event.loaded / event.total)
                        );
                        return;
                    }

                    if (event.type === HttpEventType.Response) {
                        this.uploadInProgress = false;
                        this.uploadProgress = 0;

                        const payload = {
                            fileName: file.name,
                            fileKey: fileKey,
                            fileSize: file.size,
                        };

                        this.updateInstructionsMp4Fields(payload);
                    }
                },
                error: () => {
                    this.uploadInProgress = false;
                    this.uploadProgress = 0;
                },
            });
    }

    public updateInstructionsMp4Fields(payload: any) {
        this.form.get("instructionsMP4").setValue(payload);
        this.form.get("instructionsMp4Url").setValue(payload.fileKey);
    }

    get getInstructionsMp4FileName() {
        return (
            this.form.get("instructionsMP4")?.value?.fileName ?? "Selecionar arquivo"
        );
    }

    get instructionsMp4Url() {
        return this.form.get("instructionsMp4Url")?.value;
    }

    confirmOpenAdmissionFields() {
        const modalRef = this._modalService.open(ConfirmDialogComponent, {
            animation: false,
            centered: true,
        });

        modalRef.componentInstance.type = ConfirmDialogEnum.SWITCH_ALERT;
        modalRef.componentInstance.title = "Prosseguir com Alteração de Campos";
        modalRef.componentInstance.messageConfirm = "Continuar";
        modalRef.componentInstance.messageCancel = "Cancelar";
        modalRef.componentInstance.icon = "info";
        modalRef.componentInstance.message = `Alterar os campos pode impactar o processo de admissão e candidatura. Deseja continuar?`;

        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.drawerState = true;
            }
        }).catch(() => { });
    }


    setFormFields() {
        const data = {
            applicantFormFields: [
                //{ key: 'domain', label: 'Domínio' },
                //{ key: 'name', label: 'Nome' }, // mandatário
                { key: 'nickname', label: 'Nome do Crachá' },
                //{ key: 'birthDate', label: 'Data de Nascimento' }, // mandatário
                //{ key: 'phoneNumber', label: 'Telefone' }, // mandatário
                { key: 'email', label: 'Email' },
                //{ key: 'cpf', label: 'CPF' }, // mandatário
                { key: 'nis', label: 'NIS' },
                //{ key: 'dateStart', label: 'Data de Início' }, // mandatário
                //{ key: 'companyId', label: 'Empresa' }, // mandatário
                //{ key: 'companyCnpj', label: 'CNPJ da Empresa' },
                //{ key: 'companyName', label: 'Nome da Empresa' },
                //{ key: 'companyGroupId', label: 'ID do Grupo' },
                //{ key: 'jobTitleId', label: 'Função' }, // mandatário
                //{ key: 'jobTitleCode', label: 'Código da Função' },
                //{ key: 'jobTitleName', label: 'Nome da Função' },
                { key: 'salaryRangeId', label: 'Faixa Salarial', info: 'Exibido apenas se houver tabela de faixa salarial na função selecionada.' },
                //{ key: 'salaryScaleId', label: 'ID da Tabela Salarial' },
                //{ key: 'salaryScaleCode', label: 'Código da Tabela Salarial' },
                //{ key: 'jobTitleSalary', label: 'Salário' }, // mandatário
                { key: 'monthlyHoursWorked', label: 'Horas Mensais Trabalhadas' },
                { key: 'advancePercentage', label: 'Percentual de Adiantamento' },
                { key: 'departmentId', label: 'Departamento' },
                //{ key: 'departmentCode', label: 'Código do Departamento' },
                //{ key: 'costCenterId', label: 'Centro de Custo' }, // mandatário
                //{ key: 'costCenterCode', label: 'Código do Centro de Custo' },
                //{ key: 'timeShiftId', label: 'Turno' }, // mandatário
                //{ key: 'timeShiftCode', label: 'Código do Turno' },
                //{ key: 'workRegime', label: 'Regime de Contrato' }, // mandatário
                { key: 'vacancyType', label: 'Tipo de Vaga' },
                //{ key: 'isPcd', label: 'É PCD?' }, // mandatário
                { key: 'pcdType', label: 'Tipo de Deficiência', info: 'Exibido apenas se o candidato for PCD.' },
                { key: 'eSocialPcdType', label: 'Tipo de Deficiência - ESocial', info: 'Exibido apenas se o candidato for PCD.' },
                //{ key: 'contractualTerm', label: 'Prazo Contratual' }, // mandatário
                { key: 'employeeType', label: 'Tipo de Funcionário', info: 'Exibido apenas se o prazo contratual for do tipo "determinado".' },
                { key: 'modalityIndicative', label: 'Indicativo de Modalidade', info: 'Exibido apenas se o tipo de funcionário for "aprendiz".' },
                { key: 'inscriptionType', label: 'Tipo de Inscrição', info: 'Exibido apenas se o tipo de funcionário for "aprendiz" e a modalidade for "indireta".' },
                { key: 'inscriptionNumber', label: 'Número de Inscrição', info: 'Exibido apenas se o tipo de funcionário for "aprendiz" e a modalidade for "indireta".' },
                { key: 'qualifierEntityCNPJ', label: 'CNPJ da Entidade Qualificadora', info: 'Exibido apenas se o tipo de funcionário for "aprendiz".' },
                { key: 'internshipNature', label: 'Natureza do Estágio', info: 'Exibido apenas se o tipo de funcionário for "estagiário".' },
                { key: 'internshipLevel', label: 'Nível do Estágio', info: 'Exibido apenas se o tipo de funcionário for "estagiário".' },
                { key: 'internshipInsurancePolicy', label: 'Número Apólice de Seguro do Estágio', info: 'Exibido apenas se o tipo de funcionário for "estagiário".' },
                { key: 'internshipWorkArea', label: 'Área de Atuação do Estágio', info: 'Exibido apenas se o tipo de funcionário for "estagiário".' },
                { key: 'educationalEntity', label: 'Entidade Educacional', info: 'Exibido apenas se o tipo de funcionário for "estagiário".' },
                { key: 'supervisorName', label: 'Nome do Supervisor', info: 'Exibido apenas se o tipo de funcionário for "estagiário".' },
                { key: 'contractEnd', label: 'Término do Contrato', info: 'Exibido apenas se o prazo contratual for "determinado".' },
                { key: 'recruiterName', label: 'Nome do Recrutador', info: 'Exibido apenas se o contato do recrutador também for solicitado (opcional ou obrigatório).' },
                { key: 'recruiterContact', label: 'Contato do Recrutador', info: 'Exibido apenas se o nome do recrutador também for solicitado (opcional ou obrigatório).' },
                { key: 'notice', label: 'Observações' },
                { key: 'journeyType', label: 'Posição de Trabalho' },
                { key: 'contractType', label: 'Tipo de Contrato' },
                { key: 'zipCode', label: 'CEP' },
                { key: 'neighborhood', label: 'Bairro' },
                { key: 'city', label: 'Cidade' },
                { key: 'state', label: 'Estado' },
            ],
            admissionFormFields: [
                //{ key: 'birthDate', label: 'Data Nascimento' }, // mandatário
                { key: 'maritalStatus', label: 'Estado Civil' },
                { key: 'sex', label: 'Sexo' },
                { key: 'socialName', label: 'Nome Social' },
                { key: 'dependentNumber', label: 'Número de Dependentes' },
                { key: 'stranger', label: 'Estrangeiro' },
                { key: 'nationality', label: 'Nacionalidade' },
                { key: 'birthState', label: 'UF de Origem' },
                //{ key: 'birthplace', label: 'Naturalidade' }, // mandatário
                { key: 'racialDistinction', label: 'Raça/Cor' },
                { key: 'schooling', label: 'Escolaridade' },
                //{ key: 'mothersName', label: 'Nome da Mãe' }, // mandatário
                { key: 'fathersName', label: 'Nome do Pai' },
                { key: 'bankNumber', label: 'Número do Banco' },
                { key: 'bankAgency', label: 'Agência Bancária' },
                { key: 'bankAccountNumber', label: 'Número da Conta Bancária' },
                { key: 'bankAccountDigit', label: 'Dígito da Conta Bancária' },
                { key: 'zipCode', label: 'CEP' },
                { key: 'streetType', label: 'Tipo de Logradouro' },
                { key: 'street', label: 'Logradouro' },
                { key: 'addressComplement', label: 'Complemento do Endereço' },
                { key: 'houseNumber', label: 'Número da Residência' },
                { key: 'neighborhood', label: 'Bairro' },
                { key: 'city', label: 'Cidade' },
                { key: 'state', label: 'Estado' },
                //{ key: 'email', label: 'Email' }, // mandatário
                //{ key: 'contact1', label: 'Contato Principal' }, // mandatário
                { key: 'emergencyContact', label: 'Telefone do Contato de Emergência' },
                { key: 'emergencyName', label: 'Nome do Contato de Emergência' },
                { key: 'emergencyKinship', label: 'Grau de Parentesco do Contato de Emergência' },
                { key: 'emergencyEmail', label: 'Email do Contato de Emergência' },
                { key: 'shirtSize', label: 'Tamanho da Blusa' },
                { key: 'pantsSize', label: 'Tamanho da Calça' },
                { key: 'shoeSize', label: 'Número do Calçado' },
                { key: 'electoralCard', label: 'Número do Título de Eleitor' },
                { key: 'electoralSection', label: 'Seção do Título de Eleitor' },
                { key: 'electoralZone', label: 'Zona do Título de Eleitor' },
                //{ key: 'rg', label: 'RG' }, // mandatário
                { key: 'rgIssuer', label: 'Órgão Emissor do RG' },
                { key: 'rgState', label: 'Estado do RG' },
                { key: 'rgDate', label: 'Data de Emissão do RG' },
                { key: 'pis', label: 'Número do PIS' },
                { key: 'ctpsNumber', label: 'Número da CTPS' },
                { key: 'ctpsSerie', label: 'Série da CTPS' },
                { key: 'ctpsState', label: 'Estado da CTPS' },
                { key: 'ctpsIssueDate', label: 'Data de Emissão da CTPS' },
                //{ key: 'cpf', label: 'CPF' }, // mandatário e apenas leitura
                { key: 'militaryDischarge', label: 'Certificado de Reservista', info: 'Exibido apenas se o sexo selecionado for "masculino".' },

                // dependentes
                //{ key: 'name', label: 'Nome do Dependente' }, // mandatário
                //{ key: 'birthDate', label: 'Data Nascimento do Dependente' }, // mandatário 
                //{ key: 'kinshipDegree', label: 'Grau de Parentesco do Dependente' }, // mandatário
                //{ key: 'sex', label: 'Sexo do Dependente' }, // mandatário
                //{ key: 'cpf', label: 'CPF do Dependente' }, // mandatário
                { key: 'familySalaryType', label: 'Dependente de Salário Família' },
                { key: 'incomeReportType', label: 'Dependente de Imposto de Renda' },
            ]
        };

        this.applicantMetadata = data.applicantFormFields;
        this.admissionMetadata = data.admissionFormFields;
    }
}
